// src/pages/league/BrowseLeaguesPage.tsx — the front door for people with no league.
//
// Twenty of sixty users signed up and never joined anything: every league was
// invite-only and there was nowhere to look. The discover endpoint already
// existed and nothing called it; LeagueSettingsPage even offered to "List
// publicly in league discovery" — a toggle pointing at a page that was never
// built.
//
// Joining routes to the existing /join/:code flow rather than duplicating the
// preview-and-name-your-team step. A public league's code is not a secret.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { PublicLeague } from '../../types';
import BackLink from '../../components/common/BackLink';

export default function BrowseLeaguesPage() {
  // Reachable logged-out at /browse, so nothing here may assume an account.
  const { user } = useAuth();
  const [leagues, setLeagues] = useState<PublicLeague[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (term: string) => {
    setLoading(true);
    setError(null);
    try {
      const page = await apiService.discoverPublicLeagues(term ? { search: term } : {});
      setLeagues(page.leagues);
      setTotal(page.total);
    } catch (e: any) {
      setError(e.message || 'Could not load leagues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load(search.trim());
  };

  return (
    <div>
      {user
        ? <BackLink to="/app/leagues">Back to My Leagues</BackLink>
        : <BackLink to="/">Back to OnlyKickers</BackLink>}

      <div className="border-b-2 border-ink-900 pb-4 mt-4 mb-8">
        <h1 className="font-display uppercase text-ink-900 text-4xl leading-none">Open Leagues</h1>
        <p className="text-secondary-600 mt-1">
          Leagues taking members right now. Anyone can join — no invite needed.
          {!user && ' Pick one and we will get you signed up.'}
        </p>
      </div>

      <form onSubmit={onSearch} className="flex gap-2 mb-6">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name"
          aria-label="Search leagues by name"
          className="form-input flex-1"
        />
        <button type="submit" className="btn-primary uppercase tracking-wider px-5">
          Search
        </button>
      </form>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="loading-spinner w-6 h-6 mr-3" />
          <span className="text-secondary-500 text-sm">Loading leagues…</span>
        </div>
      )}

      {!loading && error && <p className="text-sm text-danger-700 py-4">{error}</p>}

      {!loading && !error && leagues.length === 0 && (
        <div className="border-2 border-ink-900 bg-secondary-50 p-6 text-center">
          <p className="font-display uppercase text-ink-900 text-xl leading-none">
            {search ? 'Nothing matches that' : 'No open leagues yet'}
          </p>
          <p className="text-sm text-secondary-600 mt-2">
            {search
              ? 'Try a different name, or start your own.'
              : 'Every league is invite-only at the moment. Start one and it can be listed here.'}
          </p>
          <Link
            to={user ? '/app/leagues/create' : '/register'}
            className="btn-primary inline-block mt-4 uppercase tracking-wider"
          >
            {user ? 'Create a league' : 'Sign up to start one'}
          </Link>
        </div>
      )}

      {!loading && !error && leagues.length > 0 && (
        <>
          <p className="text-xs uppercase tracking-wider text-secondary-500 mb-3">
            {total} {total === 1 ? 'league' : 'leagues'} taking members
          </p>
          <div className="bg-white border border-secondary-200 divide-y divide-secondary-200">
            {leagues.map((l) => (
              <div key={l.id} className="p-4 flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0">
                  <p className="font-semibold text-ink-900" data-testid="public-league-name">{l.name}</p>
                  <p className="text-xs text-secondary-500 mt-0.5">
                    <span className="font-display tabular-nums text-sm text-ink-900">
                      {l.spots_available}
                    </span>{' '}
                    {l.spots_available === 1 ? 'spot' : 'spots'} left
                    {' · '}{l.team_count}/{l.max_teams ?? '—'} teams
                    {l.commissioner_name ? ` · run by ${l.commissioner_name}` : ''}
                  </p>
                  {l.description && (
                    <p className="text-sm text-secondary-600 mt-1 line-clamp-2">{l.description}</p>
                  )}
                </div>
                {l.invite_code ? (
                  <Link
                    to={`/join/${l.invite_code}`}
                    aria-label={`Join ${l.name}`}
                    className="btn px-4 py-1.5 text-xs font-semibold uppercase tracking-wider border border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-paper shrink-0"
                  >
                    Join
                  </Link>
                ) : (
                  <span className="text-xs text-secondary-400 shrink-0">Not joinable</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
